
import React, { useEffect, useState, useRef } from "react";
import { GoogleMap, LoadScript } from "@react-google-maps/api";
import { GoogleMapsOverlay } from "@deck.gl/google-maps";
import { ScatterplotLayer } from "@deck.gl/layers";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Avatar,
  alpha,
  useTheme,
  CircularProgress,
  Alert,
  Paper,
  Chip,
} from "@mui/material";
import {
  MdMap,
  MdError,
  MdVisibility,
  MdPeople,
  MdTrendingUp,
} from "react-icons/md";
import axios from "axios";

// Dot colours by local customer density: cyan = isolated, navy = dense
// (same ramp as the classic Google Maps heatmap gradient).
const DOT_COLORS = [
  [0, 255, 255, 220],
  [0, 191, 255, 220],
  [0, 127, 255, 220],
  [0, 63, 255, 220],
  [0, 0, 255, 220],
  [0, 0, 223, 230],
  [0, 0, 191, 240],
  [0, 0, 159, 255],
];

const HeatmapComponent = ({
  apiUrl = "https://api.medstown.com/customer/heatmap",
  defaultZoom = 8,
  apiKey = "AIzaSyBjgK-bezlAjIF6PUj_239Ln9jYSaO_A30",
}) => {
  const theme = useTheme();
  const [heatmapData, setHeatmapData] = useState([]);
  const [rawHeatmapData, setRawHeatmapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 17.416889, lng: 78.43867 });
  const [mapInstance, setMapInstance] = useState(null);
  const overlayRef = useRef(null);

  // Medstown brand colors with validation
  const medstownColors = {
    primary: "#014D4D",
    secondary: "#02A0A0",
    accent: "#FFB800",
    success: "#4caf50",
    error: "#f44336",
    warning: "#ff9800",
  };

  // Helper function to safely get colors
  const getColor = (colorKey, fallback = '#014D4D') => {
    const color = medstownColors[colorKey];
    if (!color || typeof color !== 'string') {
      console.warn(`Invalid color for ${colorKey}, using fallback:`, color);
      return fallback;
    }
    return color;
  };

  // Safe alpha function wrapper
  const safeAlpha = (color, opacity) => {
    try {
      return alpha(color, opacity);
    } catch (error) {
      console.warn('Alpha function error, using fallback:', error);
      return `rgba(1, 77, 77, ${opacity})`; // Fallback with primary color
    }
  };

  const mapContainerStyle = {
    width: "100%",
    height: "280px",
    borderRadius: "8px",
  };

  // Function to format raw data into plain {lat, lng} points
  const formatHeatmapData = (rawData) => {
    if (!rawData || !Array.isArray(rawData)) {
      console.warn('Invalid raw data provided:', rawData);
      return [];
    }

    const formattedData = rawData
      .filter(item => {
        const hasValidLat = item.lat && !isNaN(parseFloat(item.lat));
        const hasValidLng = item.lng && !isNaN(parseFloat(item.lng));
        return hasValidLat && hasValidLng;
      })
      .map((item) => ({
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lng),
      }));

    console.log("✅ Successfully formatted heatmap data:", formattedData.length, "points");
    return formattedData;
  };

  // Format data as soon as it arrives (no Google Maps dependency anymore)
  useEffect(() => {
    if (rawHeatmapData.length > 0) {
      setHeatmapData(formatHeatmapData(rawHeatmapData));
    }
  }, [rawHeatmapData]);

  // Attach/update the deck.gl heatmap overlay on the map
  useEffect(() => {
    if (!mapInstance) return;

    if (!overlayRef.current) {
      overlayRef.current = new GoogleMapsOverlay({});
      overlayRef.current.setMap(mapInstance);
    }

    // Count customers per ~1km grid cell so each dot can be coloured by
    // local density, like the classic heatmap look.
    const cellKey = (d) => `${d.lat.toFixed(2)},${d.lng.toFixed(2)}`;
    const cellCounts = new Map();
    heatmapData.forEach((d) => {
      const key = cellKey(d);
      cellCounts.set(key, (cellCounts.get(key) || 0) + 1);
    });

    overlayRef.current.setProps({
      layers: [
        new ScatterplotLayer({
          id: "customer-points",
          data: heatmapData,
          getPosition: (d) => [d.lng, d.lat],
          getFillColor: (d) =>
            DOT_COLORS[
              Math.min(cellCounts.get(cellKey(d)) || 1, DOT_COLORS.length) - 1
            ],
          radiusUnits: "pixels",
          getRadius: 4,
          stroked: false,
        }),
      ],
    });
  }, [mapInstance, heatmapData]);

  // Clean up the overlay on unmount
  useEffect(() => {
    return () => {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current.finalize();
        overlayRef.current = null;
      }
    };
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("🔄 Fetching heatmap data from:", apiUrl);
      const response = await axios.get(apiUrl);
      console.log("📡 API Response structure:", {
        success: response.data?.success,
        dataLength: response.data?.heatmapData?.length,
        sampleData: response.data?.heatmapData?.[0]
      });

      if (response.data && response.data.success) {
        if (response.data.heatmapData && Array.isArray(response.data.heatmapData)) {
          console.log("✅ Raw heatmap data received:", response.data.heatmapData.length, "points");
          setRawHeatmapData(response.data.heatmapData);
        } else {
          console.warn("⚠️ No heatmap data in API response");
          setError("No heatmap data available from the server");
        }
      } else {
        const errorMessage = response.data?.message || "Unknown server error";
        console.error("❌ API returned success: false", errorMessage);
        setError("Server error: " + errorMessage);
      }
    } catch (error) {
      console.error("💥 Error fetching heatmap data:", error);
      if (error.response) {
        setError(`Server responded with ${error.response.status}: ${error.response.statusText}`);
      } else if (error.request) {
        setError("Network error: Unable to reach the server");
      } else {
        setError("Request error: " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [apiUrl]);

  // API key validation
  if (!apiKey || typeof apiKey !== 'string') {
    return (
      <Card sx={{ 
        borderRadius: 2, 
        border: `1px solid ${safeAlpha(getColor('error'), 0.2)}` 
      }}>
        <CardContent sx={{ p: 1.5 }}>
          <Alert severity="error" sx={{ borderRadius: 1.5 }}>
            ❌ Google Maps API key is missing or invalid. Please configure your API key.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${safeAlpha(getColor('primary'), 0.08)}`,
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Header */}
      <Box sx={{
        p: 1.5,
        backgroundColor: safeAlpha(getColor('primary'), 0.06),
        borderBottom: `1px solid ${safeAlpha(getColor('primary'), 0.1)}`
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{
            backgroundColor: safeAlpha(getColor('primary'), 0.1),
            color: getColor('primary'),
            width: 28,
            height: 28
          }}>
            <MdMap size={16} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="body1" 
              fontWeight={700} 
              sx={{ 
                fontSize: '0.9rem',
                color: getColor('primary')
              }}
            >
              Customer Distribution Heatmap
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ fontSize: '0.7rem' }}
            >
              {loading 
                ? "Loading..." 
                : `${heatmapData.length} data points`
              }
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={1}>
            <Chip
              label={mapsLoaded ? "Map Ready" : "Loading Map"}
              size="small"
              sx={{
                backgroundColor: safeAlpha(
                  getColor(mapsLoaded ? 'success' : 'warning'), 
                  0.1
                ),
                color: getColor(mapsLoaded ? 'success' : 'warning'),
                fontSize: '0.65rem',
                height: 22,
                fontWeight: 600
              }}
            />
          </Stack>
        </Stack>
      </Box>

      <CardContent sx={{ p: 1.5 }}>
        {/* Loading State */}
        {loading && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Stack alignItems="center" spacing={1.5}>
              <CircularProgress 
                size={32}
                sx={{ color: getColor('primary') }}
              />
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ fontSize: '0.8rem' }}
              >
                Loading heatmap data...
              </Typography>
            </Stack>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 1.5, 
              borderRadius: 1.5,
              fontSize: '0.8rem'
            }}
            icon={<MdError size={16} />}
          >
            {error}
          </Alert>
        )}

        {/* Map Container */}
        {!loading && !error && (
          <Box sx={{ position: 'relative' }}>
            <LoadScript
              googleMapsApiKey={apiKey}
              onLoad={() => {
                console.log("🗺️ Google Maps loaded successfully");
                setMapsLoaded(true);
              }}
              onError={(error) => {
                console.error("💥 Google Maps loading error:", error);
                setError("Failed to load Google Maps. Please check your API key permissions and internet connection.");
                setMapsLoaded(false);
              }}
              loadingElement={
                <Box sx={{ 
                  height: mapContainerStyle.height,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: safeAlpha(theme.palette.grey[100], 0.5),
                  borderRadius: 2
                }}>
                  <Stack alignItems="center" spacing={1}>
                    <CircularProgress 
                      size={24} 
                      sx={{ color: getColor('primary') }} 
                    />
                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                      Loading Google Maps...
                    </Typography>
                  </Stack>
                </Box>
              }
            >
              <Box sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={defaultZoom}
                  onLoad={(map) => {
                    console.log("🗺️ Map instance loaded successfully");
                    setMapInstance(map);
                  }}
                  onUnmount={() => {
                    setMapInstance(null);
                    if (overlayRef.current) {
                      overlayRef.current.setMap(null);
                      overlayRef.current.finalize();
                      overlayRef.current = null;
                    }
                  }}
                  onError={(error) => {
                    console.error("💥 Map instance error:", error);
                    setError("Map rendering failed. Please try refreshing the page.");
                  }}
                  options={{
                    fullscreenControl: true,
                    streetViewControl: false,
                    mapTypeControl: true,
                    zoomControl: true,
                    styles: [
                      {
                        featureType: "all",
                        elementType: "geometry",
                        stylers: [{ color: "#f5f5f5" }]
                      },
                      {
                        featureType: "water",
                        elementType: "geometry",
                        stylers: [{ color: "#e9e9e9" }]
                      }
                    ]
                  }}
                />
              </Box>
            </LoadScript>
          </Box>
        )}

        {/* Data Summary */}
        {heatmapData.length > 0 && mapsLoaded && (
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            alignItems="center" 
            sx={{ mt: 1.5 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{
                backgroundColor: safeAlpha(getColor('success'), 0.1),
                color: getColor('success'),
                width: 20,
                height: 20
              }}>
                <MdVisibility size={12} />
              </Avatar>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                {heatmapData.length} data points displayed
              </Typography>
            </Stack>
            
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ fontSize: '0.65rem' }}
            >
              Updated: {new Date().toLocaleTimeString()}
            </Typography>
          </Stack>
        )}

        {/* Color Legend */}
        {heatmapData.length > 0 && mapsLoaded && (
          <Paper
            elevation={0}
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: safeAlpha(getColor('primary'), 0.03),
              border: `1px solid ${safeAlpha(getColor('primary'), 0.08)}`
            }}
          >
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar sx={{
                  backgroundColor: safeAlpha(getColor('accent'), 0.1),
                  color: getColor('accent'),
                  width: 20,
                  height: 20
                }}>
                  <MdTrendingUp size={12} />
                </Avatar>
                <Typography 
                  variant="caption" 
                  fontWeight={600} 
                  sx={{ fontSize: '0.75rem' }}
                >
                  Density Legend
                </Typography>
              </Stack>
              
              <Box sx={{ position: 'relative' }}>
                <Box
                sx={{
                  height: 12,
                  borderRadius: 1,
                  background: `linear-gradient(to right,
                    rgba(0, 255, 255, 1),
                    rgba(0, 191, 255, 1),
                    rgba(0, 127, 255, 1),
                    rgba(0, 63, 255, 1),
                    rgba(0, 0, 255, 1),
                    rgba(0, 0, 223, 1),
                    rgba(0, 0, 191, 1),
                    rgba(0, 0, 159, 1)
                  )`,
                  border: `1px solid ${safeAlpha(theme.palette.divider, 0.2)}`
                }}
                
                />
                
                <Stack 
                  direction="row" 
                  justifyContent="space-between" 
                  sx={{ mt: 0.5 }}
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.65rem',
                      color: 'text.secondary',
                      fontWeight: 500
                    }}
                  >
                    Low Density
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.65rem',
                      color: 'text.secondary',
                      fontWeight: 500
                    }}
                  >
                    High Density
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </Paper>
        )}

        {/* No Data State */}
        {!loading && !error && heatmapData.length === 0 && rawHeatmapData.length === 0 && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 200,
            flexDirection: 'column',
            gap: 1.5
          }}>
            <Avatar sx={{
              backgroundColor: safeAlpha(getColor('primary'), 0.1),
              color: getColor('primary'),
              width: 48,
              height: 48
            }}>
              <MdPeople size={24} />
            </Avatar>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ fontSize: '0.85rem' }}
            >
              No heatmap data available
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ fontSize: '0.7rem' }}
            >
              Customer location data will appear here when available
            </Typography>
          </Box>
        )}

        {/* Debug Information (Development Only) */}
        {process.env.NODE_ENV === 'development' && (
          <Box sx={{ 
            mt: 1.5, 
            p: 1, 
            backgroundColor: safeAlpha('#000', 0.05), 
            borderRadius: 1,
            fontSize: '0.65rem',
            fontFamily: 'monospace'
          }}>
            {/* <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
              Debug: Maps: {mapsLoaded.toString()} | Raw: {rawHeatmapData.length} | Formatted: {heatmapData.length} | Loading: {loading.toString()}
            </Typography> */}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default React.memo(HeatmapComponent);


